/**
 * The webmail's one composer (DESIGN.md T11): state, sending and the element that the hosts place.
 *
 * - Layout: reading pane (default, like OWA), full width ("pop out") or docked; remembered per
 *   device. The mail page renders the element in its reading-pane column for every layout (one tree
 *   position, so changing the layout keeps what was typed); other pages render it themselves.
 * - Leaving: opening another message while a reading-pane composer holds changes asks to discard
 *   (Save draft / Discard / Cancel); the same applies to starting another compose.
 * - Attachments: compose-attachments.ts (uploads with progress, originals on forward, .eml items).
 */
import { useI18n, type MessageKey } from '@platform/i18n';
import {
  Composer,
  ConfirmDialog,
  errorCode,
  readComposerLayout,
  toast,
  useAsync,
  useErrorText,
  useShortcut,
  type AddressBookSource,
  type ComposerLayout,
  type ComposerValues,
  type ComposerHandle,
  type ComposerSignatureControls,
  type Recipient,
  type SenderIdentity,
} from '@platform/ui';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, type Address } from './api';
import { useComposeAttachments, type ComposeAttachments } from './compose-attachments';
import { useRecipientLabels } from './compose-labels';
import { useRecipientDirectory } from './recipient-directory';
import type { ComposeMode, ComposeRequest } from './mail-context';
import { useComposeDrafts } from './compose-drafts';
import { useComposeSignatures } from './compose-signatures';
import { useFreshFactorVerification } from '@platform/ui';

const COMPOSER_TITLE: Record<ComposeMode, MessageKey> = {
  new: 'webmail.compose.newMessage',
  draft: 'webmail.compose.newMessage',
  reply: 'webmail.compose.replyTitle',
  replyAll: 'webmail.compose.replyAllTitle',
  forward: 'webmail.compose.forwardTitle',
  forwardAsAttachment: 'webmail.compose.forwardAsAttachmentTitle',
};

/** Modes that send a new message, so mails can be attached (forward as attachment, drag from the list). */
const ATTACHING_MODES = new Set<ComposeMode>(['new', 'forwardAsAttachment', 'draft']);

const addresses = (list: readonly Recipient[]): Address[] => list.map((r) => ({ name: r.name ?? '', address: r.email }));

/** Sends what the composer holds, according to its mode. */
function sendComposed({ mode, sourceId }: ComposeRequest, values: ComposerValues, { ids }: ComposeAttachments): Promise<void> {
  const { from, body } = values;
  const recipients = { to: addresses(values.to), cc: addresses(values.cc), bcc: addresses(values.bcc) };
  const uploadIds = ids.uploadIds;
  if (ATTACHING_MODES.has(mode) || !sourceId) return api.send({ from, subject: values.subject, body, ...recipients, attachItemIds: ids.attachItemIds, uploadIds });
  if (mode === 'forward') return api.forward(sourceId, { from, body, ...recipients, originalAttachmentIds: ids.originalAttachmentIds, uploadIds });
  return api.reply(sourceId, { from, body, replyAll: mode === 'replyAll', uploadIds });
}

/** Address book source of the composer (platform API, 50 per page). */
const addressBook: AddressBookSource = {
  load: (query, filter, cursor, signal) => api.addressBook(query, filter, cursor, signal),
  photoUrl: (email) => api.photoUrl(email),
};

export interface ComposeHost {
  compose: (request?: ComposeRequest) => void;
  /** The composer element (undefined: none open). */
  composer: ReactNode;
  composerLayout: ComposerLayout | undefined;
  leaveComposer: (action: () => void) => void;
  /** Addresses the user may send as (left out of reply all). */
  identityEmails: readonly string[];
  /** Load the signatures again (after changes in Settings → Signatures). */
  reloadSignatures: () => void;
  /** Confirmation dialog for discarding; render it once. */
  dialog: ReactNode;
}

/**
 * `enabled`: shortcuts active. `preload`: the page is up and idle, so identities, signatures and attachment limits may
 * be fetched now; until then they load lazily on the first compose (which opens once they are answered).
 */
export function useComposeHost({ enabled, preload, onSent, displayTimeZone }: { enabled: boolean; preload: boolean; onSent: () => void; displayTimeZone?: string | null }): ComposeHost {
  const { t } = useI18n();
  const errorText = useErrorText();
  const verification = useFreshFactorVerification(api.mfa);
  const { recipientLabels, addressBookLabels } = useRecipientLabels();
  const [composeUsed, setComposeUsed] = useState(false);
  // Once wanted, the data stays wanted (leaving the mail page must not cancel in-flight loads).
  const [preloaded, setPreloaded] = useState(false);
  if (preload && !preloaded) setPreloaded(true);
  const dataEnabled = preloaded || composeUsed;
  const identities = useAsync(() => api.identities(), 0, dataEnabled);
  const attachments = useComposeAttachments(dataEnabled);
  const [request, setRequest] = useState<ComposeRequest & { key: number; signature?: ComposerSignatureControls | undefined }>();
  const signatures = useComposeSignatures(dataEnabled);
  const mailKeys = useAsync(() => api.mailCrypto ? api.mailCrypto.state() : Promise.resolve(undefined), request?.key ?? 0, dataEnabled);
  const queued = useRef<ComposeRequest>(undefined);
  const dataReady = signatures.ready && (identities.data !== undefined || identities.error !== undefined);
  const [layout, setLayout] = useState<ComposerLayout>(readComposerLayout);
  const composerRef = useRef<ComposerHandle>(null);
  const drafts = useComposeDrafts(request, composerRef, attachments, onSent, displayTimeZone);
  const [pending, setPending] = useState<() => void>();

  const directory = useRecipientDirectory();
  const senderIdentities = useMemo<SenderIdentity[]>(
    () => (identities.data ?? []).map((i) => ({ email: i.email, name: i.name, note: i.kind === 'shared' ? t('webmail.compose.sharedMailbox') : undefined })),
    [identities.data, t],
  );

  const { reset } = attachments;
  /** Closes the composer; uploaded files go away on the server too. */
  const close = () => {
    for (const item of attachments.items) if (item.source === 'upload' && item.ref) api.deleteUpload(item.ref).catch(() => undefined);
    reset([]);
    setRequest(undefined);
    drafts.reset(undefined);
  };

  const hasChanges = request !== undefined && (drafts.dirty || drafts.saving);
  /** Runs `action` once the open composer may go (asks when it holds changes). */
  const leaveComposer = (action: () => void) => {
    if (!hasChanges) {
      if (request) close();
      action();
      return;
    }
    setPending(() => action);
  };

  const open = (next: ComposeRequest) => {
    reset(next.attachments ?? []);
    drafts.reset(next.draft);
    const { request: prepared, controls } = signatures.prepare(next);
    setRequest({ ...prepared, key: Date.now(), signature: controls });
  };
  const compose = (next: ComposeRequest = { mode: 'new' }) => {
    if (dataReady) { leaveComposer(() => open(next)); return; }
    // First compose before the lazy data arrived: start loading, open as soon as it is answered.
    setComposeUsed(true);
    queued.current = next;
  };
  useEffect(() => {
    const next = queued.current;
    if (!next || !dataReady) return;
    queued.current = undefined;
    leaveComposer(() => open(next));
  });
  useShortcut('mail.new', () => compose(), { enabled });

  const send = async (values: ComposerValues) => {
    if (!request) return;
    if (attachments.blocked) {
      toast.error(t('webmail.compose.uploadsPending'));
      return;
    }
    try {
      const saved = await drafts.prepareSend();
      if (attachments.blocked) { toast.error(t('webmail.compose.uploadsPending')); drafts.resume(); return; }
      if (saved || request.mode === 'draft') {
        if (!saved) { drafts.resume(); return; }
        const body = drafts.input();
        const result = await api.sendDraft(saved.id, body);
        if (!result.draftDeleted) toast.info(t('webmail.draft.keptAfterSend'));
      } else await sendComposed(request, values, attachments);
    } catch (error) {
      // The composer stays open with the text, so nothing typed is lost.
      if (errorCode(error) === 'draft_changed') drafts.showConflict();
      else try { if (!await verification.handle(error)) errorText.notify(error); } catch (proofError) { errorText.notify(proofError); }
      drafts.resume();
      return;
    }
    reset([]);
    setRequest(undefined);
    drafts.reset(undefined);
    toast.success(t('webmail.compose.sentToast'));
    onSent();
  };

  const readOnly = request?.mode === 'reply' || request?.mode === 'replyAll';
  const attaching = request !== undefined && ATTACHING_MODES.has(request.mode);
  const composer = request && (
    <Composer
      key={request.key}
      note={mailKeys.error ? t('mailCrypto.deliveryStatusUnavailable') : mailKeys.data?.delivery ? (!mailKeys.data.enabled || !mailKeys.data.optedIn ? t('mailCrypto.deliveryBlocked') : t('mailCrypto.composeNote', { format: t(mailKeys.data.delivery.format === 'smime' ? 'mailCrypto.smime' : 'mailCrypto.pgp'), mode: t(mailKeys.data.delivery.mode === 'sign' ? 'mailCrypto.sign' : 'mailCrypto.signEncrypt') })) : undefined}
      initialValues={request.initial}
      readOnlyFields={readOnly ? ['to', 'cc', 'bcc', 'subject'] : request.mode === 'forward' ? ['subject'] : []}
      onSend={send}
      onDiscard={() => setPending(() => () => undefined)}
      onClose={() => leaveComposer(() => undefined)}
      composerRef={composerRef}
      onChange={drafts.changed}
      signature={request.signature}
      draft={{ onSave: () => void drafts.save(drafts.draft ? 'new' : 'normal').catch(() => undefined), saveLabel: t(drafts.draft ? 'webmail.draft.saveNew' : 'webmail.compose.saveDraft'), status: drafts.status, statusError: drafts.statusError, saving: drafts.saving }}

      layout={layout}
      onLayoutChange={setLayout}
      directory={directory}
      addressBook={addressBook}
      identities={senderIdentities}
      attachments={attachments.chips}
      onRemoveAttachment={attachments.remove}
      onAddFiles={attachments.addFiles}
      onDropMailItem={attaching ? ({ id, subject }) => attachments.addItem({ id, name: subject || t('webmail.list.noSubject') }) : undefined}
      attachmentLabels={{
        list: t('webmail.compose.attachments'),
        remove: t('webmail.compose.removeAttachment'),
        drop: t('webmail.compose.dropMessages'),
        dropFiles: t('webmail.compose.dropFiles'),
        attachFile: t('webmail.compose.attachFile'),
        uploading: t('webmail.compose.uploading'),
      }}
      labels={{
        title: t(COMPOSER_TITLE[request.mode]),
        from: t('webmail.compose.from'),
        to: t('webmail.compose.to'),
        cc: t('webmail.compose.cc'),
        bcc: t('webmail.compose.bcc'),
        showBcc: t('webmail.compose.showBcc'),
        openAddressBook: t('webmail.compose.openAddressBook'),
        recipient: recipientLabels,
        addressBook: addressBookLabels,
        subject: t('webmail.compose.subject'),
        body: t('webmail.compose.body'),
        send: t('webmail.compose.send'),
        sending: t('webmail.compose.sending'),
        discard: t('webmail.compose.discard'),
        close: t('common.close'),
        pane: t('webmail.compose.pane'),
        fullWidth: t('webmail.compose.fullWidth'),
        docked: t('webmail.compose.docked'),
        recipientsHint: t('webmail.compose.recipientsHint'),
        recipientsRequired: t('webmail.compose.recipientsRequired'),
        invalidRecipients: t('webmail.compose.invalidRecipients'),
      }}
    />
  );

  const dialog = (
    <><ConfirmDialog
      open={pending !== undefined}
      onOpenChange={(value) => !value && setPending(undefined)}
      title={t('webmail.compose.discardTitle')}
      description={t(drafts.draft ? 'webmail.draft.saveAsNewDescription' : 'webmail.compose.discardDescription')}
      confirmLabel={t(drafts.draft ? 'webmail.draft.saveNew' : 'webmail.compose.saveDraft')}
      destructive={false}
      alternative={{ label: t('webmail.compose.discardConfirm'), destructive: true, onSelect: async () => {
        try { await drafts.discard(); } catch (error) { errorText.notify(error); throw error; }
        const action = pending; close(); setPending(undefined); action?.();
      } }}
      onConfirm={async () => {
        await drafts.save(drafts.draft ? 'new' : 'normal');
        const action = pending;
        close();
        setPending(undefined);
        action?.();
      }}
    /><ConfirmDialog open={drafts.conflict} onOpenChange={value => { if (!value) drafts.cancelConflict(); }} title={t('webmail.draft.conflictTitle')} description={t('webmail.draft.conflictDescription')} confirmLabel={t('webmail.draft.saveNew')} onConfirm={async () => { await drafts.save('new'); }}/></>
  );

  const identityEmails = senderIdentities.map((identity) => identity.email);
  return { compose, composer, composerLayout: request ? layout : undefined, leaveComposer, identityEmails, dialog: <>{dialog}{verification.challenge}</>, reloadSignatures: signatures.reload };
}
